/**
 * OnboardingWizard — orchestrator for the 4-step onboarding flow
 * (016 Onboarding Wizard).
 *
 * Spec refs:
 *   - FR-001..FR-006, FR-011..FR-018
 *   - User stories US1 (forward completion) and US2 (skip + non-dismissibility)
 *
 * Renders inside the project Dialog primitive (NOT a direct
 * `@radix-ui/react-dialog` import — enforced by the
 * `no-radix-dialog-outside-primitives` architecture test). The primitive's
 * `variant="fullscreen-mobile"` swaps in mobile-fullscreen / desktop-centred
 * sizing keyed on the `--spacing-dialog-max` token.
 *
 * Step state is component-local (`currentStep` 0..3). The wizard does NOT
 * persist step progress — a hard refresh restarts from step 0, which is
 * acceptable for a 4-step flow (spec Edge Cases).
 *
 * Non-dismissibility (FR-017, FR-018):
 *   - Escape: `onEscapeKeyDown={(e) => e.preventDefault()}` blocks the
 *     default Radix close behaviour.
 *   - Backdrop click: `onInteractOutside={(e) => e.preventDefault()}`
 *     blocks the Radix outside-pointer close.
 *
 * Skip (US2): the "Ohita" button in the top bar invokes `onComplete()`
 * directly. The skip handler does NOT wait for any per-step confirmation —
 * the wizard is short enough that re-entry is not burdensome (spec).
 *
 * Completion (US1): step 3's CTA ("Siirry palveluun") invokes
 * `onComplete()`. The gate component is responsible for calling
 * `session.completeOnboarding()` and navigating to `/` — keeping
 * persistence concerns out of the wizard itself.
 *
 * All user-visible copy is resolved via `useTranslate()` (FR-040).
 */

import { type JSX, useState } from 'react';
import ytkLogo from '../assets/ytk-logo.png';
import { useTranslate } from '../i18n/useTranslate.js';
import { Dialog } from '../primitives/Dialog.js';
import { OnboardingProgress } from './OnboardingProgress.js';
import { OnboardingStep } from './OnboardingStep.js';
import { LakikaveriStep } from './steps/LakikaveriStep.js';
import { NotificationStep } from './steps/NotificationStep.js';
import { TrainingStep } from './steps/TrainingStep.js';
import { WelcomeStep } from './steps/WelcomeStep.js';

export interface OnboardingWizardProps {
  /**
   * Invoked when the member completes the final step or skips via "Ohita".
   * The gate component owns the persistence + navigation; the wizard is
   * presentation-only beyond `currentStep` book-keeping.
   *
   * NOTE: today the demo adapter's `completeOnboarding()` is synchronous,
   * so a `() => void` callback fits. When the queued identity-and-access
   * feature ships an async Entra adapter, the gate's `handleComplete`
   * becomes `async` and this prop typing widens to
   * `() => void | Promise<void>` to avoid a floating promise.
   */
  readonly onComplete: () => void;
}

const TOTAL_STEPS = 4;
type StepIndex = 0 | 1 | 2 | 3;

/** Type-safe step advancement — avoids an `as StepIndex` assertion. */
const NEXT_STEP: Record<0 | 1 | 2, StepIndex> = { 0: 1, 1: 2, 2: 3 };

// Split-screen posture (proto-aligned):
//   - Full-height teal background (`bg-onboarding-bg`) fills the dialog.
//   - Title + description sit in the upper teal area, vertically centred.
//   - A white card with `rounded-t-3xl` houses progress + illustration/toggles + CTA.
//   - The top bar (logo + skip) is absolutely positioned over the teal area.
//
// `min-h-full` keeps the wrapper scrollable on extremely short viewports
// (320×480) — see PR #26 rationale. The card uses token-backed
// `min-h-onboarding-card-min` / `max-h-onboarding-card-max` (40/50 dvh).
const contentWrapperClasses = 'relative flex min-h-full flex-col bg-onboarding-bg';
const topBarClasses =
  'absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 py-4';
const logoClasses = 'h-10 w-auto';
const skipButtonClasses =
  'rounded-md border border-foreground px-4 py-1.5 text-sm font-medium text-foreground ' +
  'hover:bg-foreground/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const titleSrOnlyClasses = 'sr-only';
const upperSectionClasses = 'flex flex-1 flex-col items-center justify-center px-6 pt-20 pb-8';
const upperContentClasses = 'w-full max-w-2xl space-y-4 text-center';
const titleClasses = 'text-3xl font-bold text-foreground';
const descriptionClasses = 'text-lg leading-relaxed text-muted-foreground px-4';

function getStepTitleKey(
  step: StepIndex,
):
  | 'onboarding.step.welcome.title'
  | 'onboarding.step.lakikaveri.title'
  | 'onboarding.step.training.title'
  | 'onboarding.step.notifications.title' {
  switch (step) {
    case 0:
      return 'onboarding.step.welcome.title';
    case 1:
      return 'onboarding.step.lakikaveri.title';
    case 2:
      return 'onboarding.step.training.title';
    case 3:
      return 'onboarding.step.notifications.title';
  }
}

function getStepBodyKey(
  step: StepIndex,
):
  | 'onboarding.step.welcome.body'
  | 'onboarding.step.lakikaveri.body'
  | 'onboarding.step.training.body'
  | 'onboarding.step.notifications.body' {
  switch (step) {
    case 0:
      return 'onboarding.step.welcome.body';
    case 1:
      return 'onboarding.step.lakikaveri.body';
    case 2:
      return 'onboarding.step.training.body';
    case 3:
      return 'onboarding.step.notifications.body';
  }
}

function renderStepContent(step: StepIndex): JSX.Element {
  switch (step) {
    case 0:
      return <WelcomeStep />;
    case 1:
      return <LakikaveriStep />;
    case 2:
      return <TrainingStep />;
    case 3:
      return <NotificationStep />;
  }
}

export function OnboardingWizard({ onComplete }: OnboardingWizardProps): JSX.Element {
  const t = useTranslate();
  const [currentStep, setCurrentStep] = useState<StepIndex>(0);

  // Step 3's CTA finalises; steps 0–2's CTA advances.
  const handleCta = (): void => {
    if (currentStep === 3) {
      onComplete();
      return;
    }
    setCurrentStep(NEXT_STEP[currentStep]);
  };

  const ctaLabelKey = currentStep === 3 ? 'onboarding.cta.complete' : 'onboarding.cta.continue';

  return (
    <Dialog.Root open modal={true}>
      <Dialog.Content
        variant="fullscreen-mobile"
        // FR-017: Escape must not dismiss — Radix fires this event before
        // its default close handler; preventDefault cancels the close.
        onEscapeKeyDown={(event) => event.preventDefault()}
        // FR-018: backdrop click must not dismiss — Radix fires this event
        // before its default close handler on outside pointer events.
        onInteractOutside={(event) => event.preventDefault()}
        // FR-017: aria-modal="true" is explicitly required by the spec.
        // Radix Dialog v1.1+ does NOT emit this attribute by default
        // (they rely on the focus trap + portal mechanism), so we pass it
        // through here so assistive tech sees the modality state directly.
        aria-modal={true}
      >
        {/* Visually-hidden title satisfies Radix Dialog's accessible-name
            requirement without competing with the step heading rendered
            inside OnboardingStep. `asChild` merges the Title slot onto a
            <span> so the document only contains one <h2> per step — the
            visible one inside OnboardingStep. */}
        <Dialog.Title asChild>
          <span className={titleSrOnlyClasses}>{t(getStepTitleKey(currentStep))}</span>
        </Dialog.Title>
        <div className={contentWrapperClasses}>
          {/* Top bar — absolute over teal background */}
          <div className={topBarClasses}>
            <img src={ytkLogo} alt="YTK" className={logoClasses} />
            <button type="button" onClick={onComplete} className={skipButtonClasses}>
              {t('onboarding.skip')}
            </button>
          </div>

          {/* Upper teal area — title + description */}
          <div className={upperSectionClasses}>
            <div className={upperContentClasses}>
              <h2 className={titleClasses}>{t(getStepTitleKey(currentStep))}</h2>
              <p className={descriptionClasses}>{t(getStepBodyKey(currentStep))}</p>
            </div>
          </div>

          {/* Bottom white card — progress + content + CTA */}
          <OnboardingStep
            progress={<OnboardingProgress currentStep={currentStep} totalSteps={TOTAL_STEPS} />}
            ctaLabel={t(ctaLabelKey)}
            onCta={handleCta}
          >
            {renderStepContent(currentStep)}
          </OnboardingStep>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
